<script>
// @ts-check
import SingleSelect from '@views/progress/reports-single-select'
import MultiSelect from '@views/progress/reports-multi-select'
import * as R from 'ramda'
import Loading from '@components/custom-loading'
import { mapState, mapMutations, mapGetters } from 'vuex'
import {
  Maybe,
  ChartSelections,
  Subjects,
  Wizard, // eslint-disable-line
  ChartSelectionCasePatternMap, // eslint-disable-line
} from '@domain'
import axios from 'axios'

async function getDataBySubjectKind(subjectKind, searchKey, currentCount) {
  try {
    subjectKind = R.toLower(subjectKind)
    const res = await axios
      .get(
        '/api/subject/' +
          subjectKind +
          '/subjects?search=' +
          searchKey +
          '&frontEndCurrentOptionsCount=' +
          currentCount
      )
      .then(response => {
        if (subjectKind === 'region') {
          response.data = R.map(v => {
            v.name = regionMapName[parseInt(v.id) - 1]
            return v
          }, response.data)
          return R.sortBy(R.prop('id'), response.data || [])
        }
        return R.sortBy(R.prop('name'), response.data || [])
      })
    return res
  } catch (error) {
    throw error
  }
}

async function getDataByParentId(parentId, searchKey, currentCount) {
  try {
    const res = await axios
      .get(
        '/api/subject/' +
          parentId +
          '/children?search=' +
          searchKey +
          '&frontEndCurrentOptionsCount=' +
          currentCount
      )
      .then(response => {
        return R.sortBy(R.prop('name'), response.data || [])
      })
    return res
  } catch (error) {
    throw error
  }
}

let plurals = {
  Region: 'Regions',
  District: 'Districts',
  Campus: 'Campuses',
}

let regionMapName = [
  'Region 01 – Edinburg',
  'Region 02 – Corpus Christi',
  'Region 03 – Victoria',
  'Region 04 – Houston',
  'Region 05 – Beaumont',
  'Region 06 – Huntsville',
  'Region 07 – Kilgore',
  'Region 08 – Mount Pleasant',
  'Region 09 – Wichita Falls',
  'Region 10 – Richardson',
  'Region 11 – Fort Worth',
  'Region 12 – Waco',
  'Region 13 – Austin',
  'Region 14 – Abilene',
  'Region 15 – San Angelo',
  'Region 16 – Amarillo',
  'Region 17 – Lubbock',
  'Region 18 – Midland',
  'Region 19 – El Paso',
  'Region 20 – San Antonio',
]

export default {
  page: {
    title: 'Chart breakdown instance',
    meta: [
      { name: 'description', content: 'choose breakdown instance page...' },
    ],
  },
  components: { SingleSelect, MultiSelect, Loading },
  props: {
    instanceName: { type: String, default: '' },
    singleChoose: { type: Boolean, default: true },
  },
  data() {
    return {
      options: [],
      regionMapOptions: [],
      selectedOptions: [],
      keyword: '',
      isFetchingData: false,
    }
  },
  computed: {
    selectDesc() {
      if (this.selectedOptions.length === 0) {
        return this.singleChoose
          ? ['You can only select 1 ' + this.instanceName]
          : ['None']
      } else {
        return R.pluck('name', this.selectedOptions)
      }
    },
    validChoose() {
      return this.selectedOptions.length > 0
    },
    getDataPromise() {
      return !this.singleChoose && this.getParentId
        ? getDataByParentId(this.getParentId, this.keyword, this.options.length)
        : getDataBySubjectKind(
            this.instanceName,
            this.keyword,
            this.options.length
          )
    },
    instanceDisplayName() {
      return this.singleChoose
        ? this.instanceName
        : plurals[this.instanceName] || this.instanceName
    },
    ...mapState('wizard', {
      /**
       * @param {Wizard} state
       * @return {string}
       */
      getParentId(state) {
        return state.chartSelection.map(cSelection =>
          cSelection.case({
            RegionVsDistricts: (r, d) =>
              r.case({
                Nothing: () => '',
                Just: v => v.id,
              }),
            DistrictVsCampuses: (d, c) =>
              d.case({
                Nothing: () => '',
                Just: v => v.id,
              }),
            _: () => '',
          })
        )[0]
      },
      /**
       * @param {Wizard} state
       * @returns {Maybe<ChartSelections>}
       */
      selectionChosen(state) {
        let that = this
        let subjects = R.map(
          Subjects[that.instanceName + 'Of'],
          R.sortBy(R.prop('name'), that.selectedOptions)
        )
        let spreadObjKey = R.toLower(
          that.singleChoose ? that.instanceName : that.instanceDisplayName
        )

        let spreadObj = R.objOf(
          spreadObjKey,
          Maybe.of(that.singleChoose ? subjects[0] : subjects)
        )
        return state.chartSelection.map(cSelection =>
          cSelection.case({
            StateVsRegions: (s, r) =>
              ChartSelections.StateVsRegionsOf({
                ...{ state: s, regions: r },
                ...spreadObj,
              }),
            Regions: r => ChartSelections.Regions(Maybe.of(subjects)),
            RegionVsDistricts: (r, d) =>
              ChartSelections.RegionVsDistrictsOf({
                ...{ region: r, districts: d },
                ...spreadObj,
              }),
            Districts: d => ChartSelections.Districts(Maybe.of(subjects)),
            DistrictVsCampuses: (d, c) =>
              ChartSelections.DistrictVsCampusesOf({
                ...{ district: d, campuses: c },
                ...spreadObj,
              }),
            Campuses: c => ChartSelections.Campuses(Maybe.of(subjects)),
          })
        )
      },
      /**
       * @param {Wizard} state
       */
      navBtnName(state) {
        return this.singleChoose
          ? 'Continue'
          : state.chartType.map(cType =>
              cType.getMetrics().case({
                Nothing: () => 'Generate Graph',
                Just: () => 'Continue',
              })
            )[0]
      },
    }),
    ...mapGetters('wizard', ['wizardNextRoute']),
  },
  ...mapState('wizard', {
    /**
     * @param {Wizard} state
     */
    created(state) {
      this.getOptions()

      let clearable = this.singleChoose

      this.save(
        state.chartSelection.map(cSelection =>
          cSelection.case({
            RegionVsDistricts: (r, d) =>
              ChartSelections.RegionVsDistrictsOf({
                region: r,
                districts: clearable ? Maybe.Nothing : d,
              }),
            DistrictVsCampuses: (d, c) =>
              ChartSelections.DistrictVsCampusesOf({
                district: d,
                campuses: clearable ? Maybe.Nothing : c,
              }),
            _: () => cSelection,
          })
        )
      )
      this.SetMetrics(Maybe.Nothing)

      let single = this.singleChoose

      /** @type {function(Maybe<Subjects>): Maybe<Subjects[]>} */
      let maybeWrap = R.ap(Maybe.of(R.of))
      /** @type {ChartSelectionCasePatternMap<Maybe<Subjects[]>>} */
      let retrieveOptionsPatternMap = {
        StateVsRegions: (s, r) => r,
        Regions: r => r,
        RegionVsDistricts: (r, d) => (single ? maybeWrap(r) : d),
        Districts: d => d,
        DistrictVsCampuses: (d, c) => (single ? maybeWrap(d) : c),
        Campuses: c => c,
      }
      this.selectedOptions = R.pipe(
        R.chain(ChartSelections.case(retrieveOptionsPatternMap)),
        v => v[0] || [],
        R.map(v => {
          return { id: v.id, name: v.name, checked: true }
        })
      )(state.chartSelection)
    },
  }),
  watch: {
    keyword: function(newVal, oldVal) {
      if (this.regionMapOptions.length > 0) {
        this.options = R.filter(
          o => R.toLower(o.name).indexOf(R.toLower(newVal)) > -1,
          this.regionMapOptions
        )
      } else {
        this.options = []
        this.getOptions()
      }
    },
  },
  methods: {
    updateKeyword(val) {
      this.keyword = val
    },
    selectNone() {
      this.selectedOptions = []
    },
    updateSelected(val) {
      if (this.singleChoose) {
        this.selectedOptions = val
      } else {
        this.selectedOptions = R.union(
          R.filter(s => s.checked, val),
          R.reject(
            s => R.find(v => !v.checked && v.id === s.id, val),
            this.selectedOptions
          )
        )
      }
    },
    loadMore() {
      this.getOptions()
    },
    saveAndNext: function() {
      this.save(this.selectionChosen)
      this.$router.push({ path: this.wizardNextRoute })
    },
    ...mapMutations('wizard', {
      save: 'SetChartSelection',
      SetMetrics: 'SetMetrics',
    }),
    getOptions() {
      var that = this
      that.isFetchingData = true
      this.getDataPromise.then(val => {
        let scrollTopRate =
          that.options.length / (that.options.length + val.length)
        that.options = R.concat(
          R.map(v => {
            v.checked = R.any(R.propEq('id', v.id), that.selectedOptions)
            return v
          }, that.options),
          R.map(v => {
            v.checked = R.any(R.propEq('id', v.id), that.selectedOptions)
            return v
          }, val)
        )
        if (that.selectedOptions.length === 0) {
          let checkedOption = R.find(v => v.checked, that.options)
          if (checkedOption) {
            that.selectedOptions.push(checkedOption)
          }
        }
        if (that.instanceName === 'Region') {
          that.regionMapOptions = that.options
        }
        that.isFetchingData = false
        setTimeout(() => {
          let scrollDiv = document.getElementById('select-options')
          scrollDiv.scrollTop = scrollDiv.scrollHeight * scrollTopRate - 12
          scrollDiv.scrollTop =
            scrollDiv.scrollTop > 0 ? scrollDiv.scrollTop : 0
        }, 200)
      })
    },
  },
}
</script>

<template>
  <v-layout column>
    <v-flex class="mt-4 mb-4">
      <span class="section-title-font">Select {{ instanceDisplayName }}</span>
    </v-flex>
    <v-flex>
      <v-layout
        row>
        <v-flex
          pr-3
          xs5>
          <SingleSelect
            v-if="singleChoose"
            :options="options"
            :fetching-data="isFetchingData"
            @updateKeyword="updateKeyword"
            @updateSelected="updateSelected"
            @loadMore="loadMore"
          />
          <MultiSelect
            v-if="!singleChoose"
            :options="options"
            :fetching-data="isFetchingData"
            :selected-count="selectedOptions.length"
            @updateKeyword="updateKeyword"
            @updateSelected="updateSelected"
            @selectNone="selectNone"
            @loadMore="loadMore"
          />
        </v-flex>
        <v-flex
          pl-3
          xs7
          style="position: relative;">
          <v-layout column>
            <v-flex>
              <span class="sub-option-content">Selected {{ instanceDisplayName }}</span>
            </v-flex>
            <v-flex>
              <div class="select-content">
                <span
                  v-for="(item,index) in selectDesc"
                  :key="index"
                  class="select-desc">{{ item }}</span>
              </div>
            </v-flex>
          </v-layout>
          <div style="position: absolute; bottom: 0; right: 20px;">
            <v-btn
              :disabled="!validChoose"
              block
              large
              style="background-color: #243f92"
              @click="saveAndNext()"
            >{{ navBtnName }}</v-btn>
          </div>
        </v-flex>
      </v-layout>
    </v-flex>
    <v-divider class="mt-5"/>
  </v-layout>
</template>

<style scoped>
.sub-option-content {
  font-size: 18px;
  font-size: 1.175rem;
  display: block;
}
.section-title-font {
  display: inline-block;
  font-size: 30px;
  font-weight: bold;
  color: #707070;
}
.select-desc {
  display: block;
  font-size: 14px;
  font-size: 1rem;
}
.select-content {
  height: 310px;
  overflow-y: auto;
}
</style>
